import React, { useEffect } from 'react';
import { useSet } from 'react-use';
import { FieldArrayWithId, useFormContext } from 'react-hook-form';
import { Checkbox } from '../../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { OptionType } from '../../../../../../CommonComponents/OPL/Select/BaseSelect/types';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency, formatNumber } from '../../../../../../CommonUtils/formatNumber';
import { IconButton } from '../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { HtmlTable } from '../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { EmptyState } from '../../../../../../CommonComponents/OPL/AsyncStates/EmptyState/EmptyState';
import { TruncatedText } from '../../../../../../CommonComponents/OPL/Text/TruncatedText';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { TaxStatusDTO } from '../../../../api/useAccountsQuery';
import { FormFields } from '../../../AccountPreferences/components/AccountForm/AccountForm.meta';
import { useAccountFormContext } from '../../../AccountPreferences/components/AccountForm/context/useAccountFormContext';
import { EXCLUDE_OPTION } from '../../../AccountPreferences/components/AccountForm/components/AssetBucketing/AssetBucketing.meta';

const READONLY_COLUMN_COUNT = 5;
const COLUMN_COUNT = 7;

interface Props {
  holdings: FieldArrayWithId<FormFields, 'bankAccount.Positions', 'key'>[];
  readonly?: boolean;
  /** View Account mode — user lacks Edit Profile access. */
  isAccountFormReadOnly?: boolean;
  availableAssetBuckets: OptionType<number>[];
  onChange?(selectedHoldingIds: string[]): void;
  onEditHolding(holdingIndex: number): void;
  onDeleteHolding(holdingId: string): void;
}

export const HoldingsTable = ({
  holdings,
  readonly,
  isAccountFormReadOnly = false,
  onChange,
  availableAssetBuckets,
  onEditHolding,
  onDeleteHolding,
}: Props) => {
  const [
    selectedHoldings,
    {
      add: selectHolding,
      has: isHoldingSelected,
      toggle: toggleHoldingSelection,
      reset: deselectAllHoldings,
    },
  ] = useSet<string>();

  const formContext = useFormContext();

  const {
    options: {
      AccountTypeOptions,
      DisplayAssetBucketing: displayAssetBucketing,
      AllowAssetBucketingCustomization: allowAssetBucketingCustomization,
      AssetBucketingByManagementStyle: assetBucketingByManagementStyle,
    },
  } = useAccountFormContext();

  const [isLinked, isIntegrated, accountType, accountTaxStatus] = formContext.watch([
    'bankAccount.IsLinked',
    'bankAccount.IsIntegrated',
    'bankAccount.AccountType',
    'bankAccount.TaxStatus',
  ]);

  const accountTypeOptions = AccountTypeOptions.find(option => option.AccountType === accountType);

  if (!accountTypeOptions) {
    throw new Error('Account type not found');
  }

  const taxStatusOptions = accountTypeOptions.AssetAllocationTaxStatuses.map(opt => ({
    label: opt.Label,
    value: opt.Value,
  }));

  const showTaxStatusOption = accountTaxStatus === TaxStatusDTO.Mix && !isLinked && !isIntegrated;

  const showRowActions =
    !isAccountFormReadOnly && (!readonly || (isLinked && isIntegrated));

  useEffect(() => {
    if (onChange) {
      onChange(Array.from(selectedHoldings));
    }
  }, [onChange, selectedHoldings]);

  return (
    <HtmlTable
      variant="embedded"
      columnSpacing="small"
      borderTop="1px solid"
      borderColor="neutral.200"
      mb="24"
    >
      <table>
        <caption className="sr-only">Holdings</caption>
        <colgroup>
          {!readonly && allowAssetBucketingCustomization && <col />}
          <col {...(readonly ? { width: '100%' } : {})} />
          <col />
          <col />
          <col />
          {displayAssetBucketing && !assetBucketingByManagementStyle && <col />}
          {!readonly && showTaxStatusOption && <col />}
          {showRowActions && <col width="70px" />}
        </colgroup>

        <thead>
          <tr>
            {!readonly && allowAssetBucketingCustomization && (
              <th>
                <Checkbox
                  checked={selectedHoldings.size === holdings.length}
                  onChange={() =>
                    selectedHoldings.size === holdings.length
                      ? deselectAllHoldings()
                      : holdings.forEach(holding => selectHolding(holding.key))
                  }
                />
              </th>
            )}
            <th>
              <Text variant="smallCaps" textAlign="left">
                Symbol
              </Text>
            </th>
            <th>
              <Text variant="smallCaps" textAlign="right">
                Quantity
              </Text>
            </th>
            <th>
              <Text variant="smallCaps" textAlign="right">
                Price
              </Text>
            </th>
            <th>
              <Text variant="smallCaps" textAlign="right">
                Value
              </Text>
            </th>

            {displayAssetBucketing && !assetBucketingByManagementStyle && (
              <th>
                <Text variant="smallCaps" textAlign="left">
                  Asset Bucketing
                </Text>
              </th>
            )}

            {!readonly && accountTaxStatus === TaxStatusDTO.Mix && (
              <th>
                <Box display="flex" css="gap: 4px; width: 170px" alignItems="center">
                  <Text variant="smallCaps" textAlign="left">
                    Tax Status
                  </Text>
                </Box>
              </th>
            )}

            {!readonly && <th>&nbsp;</th>}
          </tr>
        </thead>

        <tbody>
          {holdings.length === 0 && (
            <tr>
              <td colSpan={readonly ? READONLY_COLUMN_COUNT : COLUMN_COUNT}>
                <EmptyState mx="auto" my="48" />
              </td>
            </tr>
          )}

          {holdings.map((holding, index) => {
            return (
              <tr key={holding.key}>
                {!readonly && allowAssetBucketingCustomization && (
                  <td>
                    <Checkbox
                      checked={isHoldingSelected(holding.key)}
                      onChange={() => toggleHoldingSelection(holding.key)}
                    />
                  </td>
                )}

                <Box as="td" flexDirection="column" alignItems="flex-start">
                  <Text fontWeight={500}>{holding.Ticker}</Text>
                  <TruncatedText width="100%" maxWidth={200}>
                    {holding.Name}
                  </TruncatedText>
                </Box>

                <td>
                  <Text fontWeight={500} textAlign="right">
                    {formatNumber(holding.Quantity ?? 0, {
                      thousandSeparated: true,
                      decimalPlaces: 2,
                    })}
                  </Text>
                </td>

                <td>
                  <Text fontWeight={500} textAlign="right">
                    {holding.Price ? formatCurrency(holding.Price, { mantissa: 2 }) : ''}
                  </Text>
                </td>

                <td>
                  <Text fontWeight={500} textAlign="right">
                    {holding.MarketValue
                      ? formatCurrency(holding.MarketValue, { mantissa: 2 })
                      : ''}
                  </Text>
                </td>

                {displayAssetBucketing &&
                  !assetBucketingByManagementStyle &&
                  holding.AssetBucketIndex !== null && (
                    <td>
                      <Text fontWeight={500}>
                        {holding.AssetBucketIndex === -1
                          ? EXCLUDE_OPTION.label
                          : availableAssetBuckets[holding.AssetBucketIndex]?.label ?? 'None'}
                      </Text>
                    </td>
                  )}

                {!readonly && showTaxStatusOption && (
                  <td>
                    <Select
                      isSearchable={false}
                      isClearable
                      options={taxStatusOptions}
                      value={
                        taxStatusOptions.find(option => option.value === holding.TaxStatus) ?? null
                      }
                      onChange={async option => {
                        const newHoldings = holdings.map(holdingItem => {
                          if (holdingItem.Id === holding.Id) {
                            return {
                              ...holdingItem,
                              TaxStatus: option?.value ?? null,
                            };
                          }

                          return holdingItem;
                        });

                        formContext.setValue('bankAccount.Positions', newHoldings);
                      }}
                      isPortal
                    />
                  </td>
                )}

                {showRowActions && (
                  <Box as="td" css="white-space: nowrap">
                    {holding.IsManuallyCreated && (
                      <>
                        <IconButton
                          icon="far fa-pencil"
                          variant="subtleInactive"
                          onClick={() => onEditHolding(index)}
                          aria-label="Edit holding"
                        />

                        <IconButton
                          icon="far fa-minus-circle"
                          variant="subtleInactive"
                          onClick={() => onDeleteHolding(holding.Id)}
                          aria-label="Delete holding"
                        />
                      </>
                    )}
                  </Box>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </HtmlTable>
  );
};
